/**
 * DUEL-V3-P3: lock forms made readable (rules: systems/duelLockOpen.ts).
 * - The foe's lock reach: while the player fights a bout, a thin dashed ring at the foe's feet of the reach of its next ready
 *   lock move (ท่าล็อกเป้า): dim gold while the player is safe, red and closing its dashes while the player is opened
 *   (in a stop, stunned, guard broken) - the distance game of the payoff, both see it.
 * - The catch's white flash: held in a form with the escape still open, a white ring on the player's feet in the escape
 *   window and the keys (F+Q) over the head - guard + dodge then breaks out.
 * FX pixel rules: whole pixels, stepped, a 1-px ink under the line, no gradient or glow. Picture only.
 */
import { duelLockOpenOf, duelMatchOf } from '@nine/shared';
import { UI } from '../labels';
import { globeBend } from '../globe';
import { registerRenderPass } from './registry';

const INK = '#2a1a0a';
const GOLD = '#c9a24a';
const RED = '#d0452f';
const WHITE = '#f6f1e4';

registerRenderPass({
  id: 'duelLockReach',
  layer: 'ground',
  order: 224,
  draw({ g, world: w, iso, ox, oy }) {
    const st = duelMatchOf(w)?.current;
    const sys = duelLockOpenOf(w);
    if (!st || !sys || st.phase !== 'fight') return;
    const me = w.player;
    const foeId = st.meId === me.id ? st.foeId : st.foeId === me.id ? st.meId : -1;
    const foe = w.get(foeId);
    g.save();
    g.imageSmoothingEnabled = false;
    // The foe's lock reach (not while either is held in a form).
    const rl = foe && !foe.dead && !sys.formView(foe.id) ? sys.readyLockOf(foe.id) : null;
    if (foe && rl && rl.range > 0) {
      const n = 48;
      const lit = rl.opened;
      const dash = lit ? 3 : 2; // segments on per 4 (stepped, whole pixels)
      for (let i = 0; i < n; i++) {
        if (i % 4 >= dash) continue;
        const a = (i / n) * Math.PI * 2 + (lit ? 0 : w.time * 0.3);
        const [x, y] = iso.proj(foe.x + Math.cos(a) * rl.range, foe.y + Math.sin(a) * rl.range);
        const px = Math.round(x + ox);
        const py = Math.round(y + oy);
        g.fillStyle = INK;
        g.fillRect(px, py + 1, 2, 1);
        g.fillStyle = lit ? RED : GOLD;
        g.fillRect(px, py, 2, 1);
      }
    }
    // The catch's white flash on the held player.
    const v = sys.formView(me.id);
    if (v && v.target === me.id && v.escapable && v.t >= v.flash[0] - 0.08 && v.t <= v.flash[1]) {
      const [x, y] = iso.proj(me.x, me.y);
      const cx = Math.round(x + ox);
      const cy = Math.round(y + oy);
      const on = v.t >= v.flash[0];
      const rx = on ? 14 : 18;
      g.fillStyle = on ? WHITE : GOLD;
      for (let i = 0; i < 28; i++) {
        const a = (i / 28) * Math.PI * 2;
        g.fillRect(Math.round(cx + Math.cos(a) * rx), Math.round(cy + Math.sin(a) * rx * 0.5), 2, 2);
      }
    }
    g.restore();
  },
});

registerRenderPass({
  id: 'duelLockKeys',
  layer: 'labels',
  order: 112,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    const sys = duelLockOpenOf(w);
    const me = w.player;
    const v = sys?.formView(me.id);
    if (!v || v.target !== me.id || !v.escapable || v.t < v.flash[0] || v.t > v.flash[1]) return;
    const [sx, sy] = iso.proj(me.x, me.y);
    const x = (sx + ox) * PX;
    const y = globeBend(sx + ox, sy + oy - 62 * me.scale)[1] * PX;
    mg.save();
    mg.textAlign = 'center';
    mg.lineJoin = 'round';
    mg.font = `700 17px ${UI}`;
    mg.lineWidth = 3;
    mg.strokeStyle = 'rgba(12,9,14,.95)';
    mg.fillStyle = WHITE;
    mg.strokeText('F + Q', x, y);
    mg.fillText('F + Q', x, y);
    mg.restore();
  },
});
